import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Search, User } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ListItemSkeleton } from '../../components/shared/ClientPageSkeleton';

const ClientMessages = () => {
 const [isLoading, setIsLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState('');
 const [selectedConversation, setSelectedConversation] = useState(null);
 const [messages, setMessages] = useState([]);
 const [newMessage, setNewMessage] = useState('');

 // Mock data - replace with actual API calls
 const conversations = [
 {
 id: 1,
 name: 'Vendor Name',
 lastMessage: 'Thank you for your booking!',
 timestamp: '2 hours ago',
 unread: 2
 },
 {
 id: 2,
 name: 'Support Team',
 lastMessage: 'We have received your inquiry',
 timestamp: '1 day ago',
 unread: 0
 }
 ];

 useEffect(() => {
 // Simulate API call
 const timer = setTimeout(() => {
 setIsLoading(false);
 }, 1000);
 return () => clearTimeout(timer);
 }, []);

 const handleSendMessage = () => {
 if (newMessage.trim()) {
 // Add message sending logic here
 setNewMessage('');
 }
 };

 const layoutProps = {
 title: 'Site Messages',
 subtitle: 'Review conversations with vendors and support.',
 guideKey: 'messages',
 };

 if (isLoading) {
 return (
 <ClientPageLayout {...layoutProps}>
 <div className="product-panel overflow-hidden">
 <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)]">
 <div className="w-full lg:w-1/3 border-r border-border flex flex-col">
 <div className="p-4 border-b border-border">
 <div className="h-10 w-full bg-surface-muted rounded-md animate-pulse"></div>
 </div>
 <div className="flex-1 overflow-y-auto p-4">
 <ListItemSkeleton count={5} />
 </div>
 </div>
 <div className="flex-1 flex flex-col items-center justify-center">
 <div className="text-center">
 <div className="w-16 h-16 bg-surface-muted rounded-lg mx-auto mb-4 animate-pulse"></div>
 <div className="h-4 w-48 bg-surface-muted rounded mx-auto animate-pulse"></div>
 </div>
 </div>
 </div>
 </div>
 </ClientPageLayout>
 );
 }

 return (
 <ClientPageLayout {...layoutProps}>
 <div className="product-panel overflow-hidden">
 <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)]">
 {/* Conversations List */}
 <div className="w-full lg:w-1/3 border-r border-border flex flex-col">
 {/* Search */}
 <div className="p-4 border-b border-border">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
 <input
 type="text"
 placeholder="Search conversations..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full rounded-product-md border border-border bg-surface py-2.5 pl-10 pr-4 text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink"
 />
 </div>
 </div>

 {/* Conversations */}
 <div className="flex-1 overflow-y-auto">
 {conversations.length === 0 ? (
 <div className="p-8 text-center text-ink-tertiary">
 <MessageSquare className="w-12 h-12 mx-auto mb-4 text-ink-tertiary" />
 <p className="text-sm">No conversations yet</p>
 </div>
 ) : (
 conversations.map((conversation) => (
 <button
 type="button"
 key={conversation.id}
 onClick={() => setSelectedConversation(conversation.id)}
 className={`w-full cursor-pointer border-b border-border p-4 text-left transition-colors hover:bg-surface-muted ${ selectedConversation === conversation.id ? 'bg-surface-muted' : '' }`}
 >
 <div className="flex items-center gap-3">
 <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-product-md border border-border bg-surface-muted">
 <User className="w-5 h-5 text-ink" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center justify-between mb-1 gap-2">
 <h3 className="truncate text-sm font-semibold text-ink">
 {conversation.name}
 </h3>
 {conversation.unread > 0 && (
 <span className="inline-flex items-center rounded-full border border-border bg-ink px-2 py-0.5 text-[10px] font-semibold text-white">
 {conversation.unread}
 </span>
 )}
 </div>
 <p className="truncate text-xs text-ink-secondary">
 {conversation.lastMessage}
 </p>
 <p className="mt-1 text-xs text-ink-tertiary">
 {conversation.timestamp}
 </p>
 </div>
 </div>
 </button>
 ))
 )}
 </div>
 </div>

 {/* Messages Area */}
 <div className="flex-1 flex flex-col">
 {selectedConversation ? (
 <>
 {/* Messages */}
 <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-canvas">
 {messages.length === 0 ? (
 <div className="flex h-full items-center justify-center text-ink-tertiary">
 <p className="text-sm">No messages yet. Start the conversation!</p>
 </div>
 ) : (
 messages.map((message, index) => (
 <div
 key={index}
 className={`flex ${ message.sender === 'me' ? 'justify-end' : 'justify-start' }`}
 >
 <div
 className={`max-w-xs lg:max-w-md px-4 py-2.5 !rounded-lg ${ message.sender === 'me' ? 'bg-ink text-white' : 'border border-border bg-surface text-ink' }`}
 >
 <p className="text-sm">{message.text}</p>
 <p className="text-xs mt-1 opacity-70">
 {message.timestamp}
 </p>
 </div>
 </div>
 ))
 )}
 </div>

 {/* Message Input */}
 <div className="border-t border-border bg-surface p-4">
 <div className="flex items-center gap-2">
 <input
 type="text"
 value={newMessage}
 onChange={(e) => setNewMessage(e.target.value)}
 onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
 placeholder="Type a message..."
 className="flex-1 rounded-product-md border border-border bg-surface px-4 py-2.5 text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink"
 />
 <button
 type="button"
 onClick={handleSendMessage}
 className="inline-flex items-center gap-2 rounded-product-md bg-ink px-4 py-2.5 font-medium text-white transition-colors hover:bg-black"
 >
 <Send className="w-4 h-4" />
 <span>Send</span>
 </button>
 </div>
 </div>
 </>
 ) : (
 <div className="flex h-full items-center justify-center text-ink-tertiary">
 <div className="text-center">
 <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-product-md border border-border bg-surface-muted">
 <MessageSquare className="h-8 w-8 text-ink" />
 </div>
 <p className="text-sm">Select a conversation to start messaging</p>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </ClientPageLayout>
 );
};

export default ClientMessages;
